import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import { useAuth } from './contexts/AuthContext';

// Auth Pages
import Login from './pages/auth/Login';
import OTPVerification from './pages/auth/OTPVerification';

// Layout
import Layout from './components/layout/Layout';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import ClientsManagement from './pages/admin/ClientsManagement';
import UsersManagement from './pages/admin/UsersManagement';
import CategoriesManagement from './pages/admin/CategoriesManagement';
import HolidaysManagement from './pages/admin/HolidaysManagement';

// Team Lead Pages
import TeamLeadDashboard from './pages/teamlead/Dashboard';
import ProjectsManagement from './pages/teamlead/ProjectsManagement';
import TeamManagement from './pages/teamlead/TeamManagement';
import TeamLeadReports from './pages/teamlead/Reports';
import AIAnalysis from './pages/teamlead/AIAnalysis';

// Member Pages
import MemberDashboard from './pages/member/Dashboard';
import MemberProjects from './pages/member/Projects';
import ActivityLog from './pages/member/ActivityLog';
import Blockages from './pages/member/Blockages';

// Shared Pages
import Profile from './pages/profile/Profile';

// Client Lead Pages
import ClientLeadDashboard from './pages/clientlead/Dashboard';

// Protected Route component
const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <CircularProgress />
    </Box>
  );
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

// Role-based dashboard redirect
const DashboardRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'superadmin') return <Navigate to="/admin/dashboard" replace />;
  if (user?.role === 'teamlead') return <Navigate to="/teamlead/dashboard" replace />;
  if (user?.role === 'clientlead') return <Navigate to="/clientlead/dashboard" replace />;
  return <Navigate to="/member/dashboard" replace />;
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/verify-otp" element={<OTPVerification />} />

        {/* Redirect root */}
        <Route path="/" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardRedirect /></ProtectedRoute>} />

        {/* Admin routes */}
        <Route path="/admin" element={
          <ProtectedRoute roles={['superadmin']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="clients" element={<ClientsManagement />} />
          <Route path="users" element={<UsersManagement />} />
          <Route path="categories" element={<CategoriesManagement />} />
          <Route path="holidays" element={<HolidaysManagement />} />
        </Route>

        {/* Team Lead routes */}
        <Route path="/teamlead" element={
          <ProtectedRoute roles={['teamlead']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<TeamLeadDashboard />} />
          <Route path="clients" element={<ClientsManagement />} />
          <Route path="projects" element={<ProjectsManagement />} />
          <Route path="team" element={<TeamManagement />} />
          <Route path="reports" element={<TeamLeadReports />} />
          <Route path="ai-analysis" element={<AIAnalysis />} />
          <Route path="activities" element={<ActivityLog />} />
        </Route>

        {/* Member routes */}
        <Route path="/member" element={
          <ProtectedRoute roles={['member']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<MemberDashboard />} />
          <Route path="projects" element={<MemberProjects />} />
          <Route path="activities" element={<ActivityLog />} />
          <Route path="blockages" element={<Blockages />} />
        </Route>

        {/* Client Lead routes */}
        <Route path="/clientlead" element={
          <ProtectedRoute roles={['clientlead']}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route path="dashboard" element={<ClientLeadDashboard />} />
          <Route path="projects" element={<ProjectsManagement />} />
          <Route path="activities" element={<ActivityLog />} />
          <Route path="reports" element={<TeamLeadReports />} />
          <Route path="ai-analysis" element={<AIAnalysis />} />
        </Route>

        {/* Profile — accessible to all authenticated users */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Profile />} />
        </Route>

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
